import { NgModule } from '@angular/core';
import {
    ActivatedRouteSnapshot,
    provideRouter,
    RouteReuseStrategy,
    RouterModule,
    Routes,
    withComponentInputBinding,
    withPreloading,
    withRouterConfig,
    withViewTransitions,
} from '@angular/router';
import { AppRouteReuseStrategy } from './core/services/app-route-reuse.strategy';
import { SelectivePreloadingStrategy } from './core/services/selective-preloading.strategy';
import { FeatureLayoutComponent } from './shared/components/feature-layout/feature-layout.component';
import { AuthService } from './shared/services/auth.service';
import { depositAuthGuard } from './core/guards/deposit-auth.guard';
import { exitQuestionGuard } from './core/guards/exit-question.guard';

export const routes: Routes = [
    // Public/auth routes (no feature layout)
    {
        path: 'sign-in',
        loadComponent: () => import('./module/auth/sign-in/sign-in.component').then(m => m.SignInComponent),
        canActivate: [AuthService],
        data: { preload: true, priority: 0 }
    },
    {
        path: 'sign-up',
        loadComponent: () => import('./module/auth/sign-up/sign-up.component').then(m => m.SignUpComponent),
        canActivate: [AuthService],
        data: { preload: true, priority: 2 }
    },
    {
        path: 'forgot-password',
        loadComponent: () => import('./module/auth/forgotpassword/forgotpassword.component').then(m => m.ForgotpasswordComponent),
        canActivate: [AuthService]
    },
    {
        path: 'reset-password',
        loadComponent: () => import('./module/auth/passowrd-update/passowrd-update.component').then(m => m.PassowrdUpdateComponent),
        canActivate: [AuthService]
    },
    {
        path: 'verify-email',
        loadComponent: () => import('./module/custodymailverification/custodymailverification.component').then(m => m.CustodymailverificationComponent),
        canActivate: [AuthService]
    },
    {
        // Standalone page from the email footer "unsubscribe" link: no guard, no header/footer
        path: 'unsubscribe',
        loadComponent: () => import('./module/unsubscribe/unsubscribe.component').then(m => m.UnsubscribeComponent)
    },
    {
        path: 'borrow-register',
        loadComponent: () => import('./module/auth/borrow-register/borrow-register.component').then(m => m.BorrowRegisterComponent),
        canActivate: [AuthService]
    },
    {
        path: 'lend-register',
        loadComponent: () => import('./module/auth/lend-register/lend-register.component').then(m => m.LendRegisterComponent),
        canActivate: [AuthService]
    },
    // Shell route with feature layout
    {
        path: '',
        component: FeatureLayoutComponent,
        canActivateChild: [AuthService],
        children: [
            {
                path: '',
                redirectTo: 'dashboard',
                pathMatch: 'full'
            },
            {
                path: 'all-assets',
                loadComponent: () => import('./module/dashboard/dashboard.component').then(m => m.DashboardComponent),
                data: { preload: true, priority: 1 }
            },
            {
                path: 'dashboard',
                loadChildren: () => import('./module/new-dashboard/new-dashboard.routes').then(m => m.NEW_DASHBOARD_ROUTES),
                data: { preload: true, priority: 1 }
            },
            {
                path: 'transactions',
                loadComponent: () => import('./module/new-dashboard/transactions-details/transactions-details.component').then(m => m.TransactionsDetailsComponent),
                data: { reuseOnQueryChange: true, preload: true, priority: 3 }
            },
            {
                path: 'my-wallet',
                loadComponent: () => import('./module/youritems/youritems.component').then(m => m.YouritemsComponent),
                canDeactivate: [exitQuestionGuard],
                data: { preload: true, priority: 2 }
            },
            { path: 'borrow', loadChildren: () => import('./module/borrow/borrow.routes').then(m => m.BORROW_ROUTES), data: { preload: true, priority: 2 } },
            { path: 'lend', loadChildren: () => import('./module/lend/lend.routes').then(m => m.LEND_ROUTES), data: { preload: true, priority: 2 } },
            { path: 'borrow-history', loadChildren: () => import('./module/borrow/borrow-history/borrow-history.routes').then(m => m.BORROW_HISTORY_ROUTES), data: { preload: true, priority: 4 } },
            { path: 'loan-request', loadComponent: () => import('./module/borrow/borrow-loan/borrow-loan.component').then(m => m.BorrowLoanComponent), canDeactivate: [exitQuestionGuard], data: { preload: true, priority: 4 } },
            { path: 'borrow-detail/:id', loadComponent: () => import('./module/loan-detail/loan-detail.component').then(m => m.LoanDetailComponent), canDeactivate: [exitQuestionGuard], data: { preload: true, priority: 3 } },
            { path: 'lending-history', loadChildren: () => import('./module/lend/lending-history/lending-history.routes').then(m => m.LENDING_HISTORY_ROUTES), data: { preload: true, priority: 4 } },
            { path: 'lending-detail/:id', loadComponent: () => import('./module/loan-detail/loan-detail.component').then(m => m.LoanDetailComponent), canDeactivate: [exitQuestionGuard], data: { preload: true, priority: 3 } },
            { path: 'kyc', loadComponent: () => import('./module/kyc/kyc.component').then(m => m.KycComponent), canDeactivate: [exitQuestionGuard], data: { preload: true, priority: 4 } },
            { path: 'kyc-complete', loadComponent: () => import('./module/kyc-complete/kyc-complete.component').then(m => m.KycCompleteComponent) },
            { path: 'nft-detail/:collection/:tokenId', loadComponent: () => import('./module/nft-detail/nft-detail.component').then(m => m.NftDetailComponent), canDeactivate: [exitQuestionGuard], data: { preload: true, priority: 3 } },
            { path: 'lazy-mint/:id', loadComponent: () => import('./module/nft-detail/nft-detail.component').then(m => m.NftDetailComponent) },
            { path: 'kyc-verify/:status', loadComponent: () => import('./module/idenfy-redirection/idenfy-redirection.component').then(m => m.IdenfyRedirectionComponent) },
            { path: 'privacy-policy', loadComponent: () => import('./module/privacy/privacy.component').then(m => m.PrivacyComponent) },
            { path: 'terms-and-conditions', loadComponent: () => import('./module/terms/terms.component').then(m => m.TermsComponent) },
            { path: 'faq', loadComponent: () => import('./module/faq/faq.component').then(m => m.FaqComponent) },
            { path: 'redeem-asset', loadComponent: () => import('./module/youritems/redeem-asset/redeem-asset.component').then(m => m.RedeemAssetComponent), data: { preload: true, priority: 4 } },
            // { path: 'verify-email', loadComponent: () => import('./module/custodymailverification/custodymailverification.component').then(m => m.CustodymailverificationComponent) },
            { path: 'confirm-email', loadComponent: () => import('./module/custodymailverification/custodymailverification.component').then(m => m.CustodymailverificationComponent) },
            { path: 'collection-detail/:collectionAddress', loadComponent: () => import('./module/collection-details/collection-details.component').then(m => m.CollectionDetailsComponent), data: { preload: true, priority: 4 } },
            { path: 'resell-loan/:id', loadComponent: () => import('./module/resell-loan/resell-loan.component').then(m => m.ResellLoanComponent), canDeactivate: [exitQuestionGuard], data: { preload: true, priority: 4 } },
            // Incase old user can access the old flow, we are keeping this route for now. Once we are sure that all users are migrated to new flow, we can remove this route.
            { path: 'onboardassets', loadComponent: () => import('./module/asset-deposit/asset-deposit.component').then(m => m.AssetDepositComponent), canActivate: [depositAuthGuard], canDeactivate: [exitQuestionGuard], data: { preload: true, priority: 4 } },
            // Redesigned 3-screen deposit flow (with the onboarding fee). Runs alongside
            // /onboardassets until the new flow replaces it. Sign-in is required before the
            // form opens — see `depositAuthGuard` for why it cannot wait until submit.
            { path: 'deposit-asset', loadComponent: () => import('./module/asset-deposit/asset-deposit.component').then(m => m.AssetDepositComponent), canActivate: [depositAuthGuard], canDeactivate: [exitQuestionGuard], data: { preload: true, priority: 4 } },
            // Deposit tracker — where a user follows a submitted asset from approval through
            // shipping and receipt to minting.
            // No depositAuthGuard: a logged-out visitor stays here and sees the shared login card.
            { path: 'my-deposits', loadComponent: () => import('./module/my-deposits/my-deposits.component').then(m => m.MyDepositsComponent), data: { reuseOnQueryChange: true, preload: true, priority: 4 } },
            { path: 'introducer-earnings', loadComponent: () => import('./module/introducer-earnings/introducer-earnings.component').then(m => m.IntroducerEarningsComponent), data: { preload: true, priority: 4 } },
            { path: '**', loadComponent: () => import('./module/pagenotfound/pagenotfound.component').then(m => m.PagenotfoundComponent), pathMatch: 'full' }
        ]
    }
];

/** The deepest route of a router state: the page actually on screen. */
function pageOf(snapshot: ActivatedRouteSnapshot): ActivatedRouteSnapshot {
    let page = snapshot;
    while (page.firstChild) page = page.firstChild;
    return page;
}

@NgModule({
    // provideRouter rather than RouterModule.forRoot, because view transitions are only
    // offered as a provideRouter feature. The options are the ones forRoot had.
    //
    // No router scroll restoration: the document is no longer the scroller — the shell scrolls
    // `.app-scroll` between the fixed header and footer — so AppScrollService positions that
    // container on every navigation instead (see AppComponent).
    providers: [
        provideRouter(
            routes,
            withComponentInputBinding(),
            withRouterConfig({ onSameUrlNavigation: 'reload' }),
            withPreloading(SelectivePreloadingStrategy),
            // Page changes cross-fade (styles.css, `::view-transition-*`). Browsers without the
            // View Transition API simply switch instantly.
            withViewTransitions({
                skipInitialTransition: true,
                onViewTransitionCreated: ({ transition, from, to }) => {
                    // Same page with only its query string changed (filters, `?focus=` clean-up):
                    // fading the screen into itself would just look like a flash.
                    const before = pageOf(from);
                    const after = pageOf(to);
                    const samePage = before.routeConfig === after.routeConfig
                        && JSON.stringify(before.params) === JSON.stringify(after.params);
                    if (samePage) transition.skipTransition();
                },
            }),
        ),
        { provide: RouteReuseStrategy, useClass: AppRouteReuseStrategy },
    ],
    exports: [RouterModule]
})
export class HomeNewRoutingModule {

}
